/**
 * Generate a URL-friendly slug from a title + ID.
 * Uses "--" as separator between slug and ID to avoid ambiguity.
 * Example: slugify("Vélo électrique", "listing_abc123") => "velo-electrique--listing_abc123"
 */
export const slugify = (text, id) => {
  if (!id) return '';
  const slug = (text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .substring(0, 60);
  return slug ? `${slug}--${id}` : id;
};

// Known ID prefixes used in the database
const ID_PREFIXES = ['listing_', 'asso_', 'user_', 'tx_', 'conv_'];

/**
 * Extract the ID from a slug-based URL parameter.
 * Priority:
 *   1. Double-dash separator: "velo--listing_abc123" => "listing_abc123"
 *   2. Known prefix detection: "velo-listing_abc123" => "listing_abc123" (old format compat)
 *   3. RNA format (W prefix): "asso-name-W123" => "W123" if starts with W + digits
 *   4. Fallback: return the whole param as-is (plain ID or unknown format)
 */
export const extractId = (slugOrId) => {
  if (!slugOrId) return '';

  // 1. Double-dash separator (canonical format)
  const sepIdx = slugOrId.indexOf('--');
  if (sepIdx !== -1) {
    return slugOrId.substring(sepIdx + 2).replace(/^-+/, '');
  }

  // 2. Detect known ID prefix anywhere in the string (old format compat)
  for (const prefix of ID_PREFIXES) {
    const prefixIdx = slugOrId.indexOf(prefix);
    if (prefixIdx !== -1) {
      return slugOrId.substring(prefixIdx);
    }
  }

  // 3. RNA format: starts with W followed by digits
  const rnaMatch = slugOrId.match(/-?(W\d{5,})$/);
  if (rnaMatch) return rnaMatch[1];

  // 4. Fallback: entire param is the ID
  return slugOrId;
};
